/**
 * Filing of Expedited Application - Step 15
 * Active only when 1u) Expedited Application is YES from Step 7 Confirmation.
 * Single date field: 3ca) Date of Filing of Expedited Application.
 */
import { useState, useEffect } from "react";
import DateTimePicker from "../../components/DateTimePicker";
import {
  getExpeditedFilingStatus,
  submitExpeditedFiling,
  sendExpeditedReceiptToClient,
  sendExpeditedFilingAutomatedMessage,
  getLeadDetails,
  buildWhatsAppLink,
  uploadAcknowledgementPdf,
  saveAcknowledgementPdfKey,
  type ExpeditedFilingStatus,
} from "../../api/discussion";
import { toast } from "sonner";
import { formatPhoneInput, validateMobileNumber } from "../../utils/phone";
import InactiveStepView from "./InactiveStepView";
import FilePreviewModal from "../../components/FilePreviewModal";

const LABEL = "text-[#222222] text-xs leading-5 font-normal";
const INPUT =
  "w-full h-9 px-2.5 py-2 text-xs leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const INPUT_GHOST =
  "h-9 px-2.5 py-2 text-xs leading-5 text-[#6D6D6D] bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px]";

function parseDDMMYYYYToDate(s: string | null | undefined): Date | null {
  if (!s || typeof s !== "string") return null;
  const parts = s.split(/[-\/]/).map(Number);
  if (parts.length < 3) return null;
  const [d, m, y] = parts;
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

function formatDateToISO(date: Date | null): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type FilingOfExpeditedApplicationViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function FilingOfExpeditedApplicationView({
  leadId,
  onSuccess,
}: FilingOfExpeditedApplicationViewProps) {
  const [status, setStatus] = useState<ExpeditedFilingStatus | null>(null);
  const [loading, setLoading] = useState(!!leadId);

  const [dateOfFilingOfExpedited, setDateOfFilingOfExpedited] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [whatsAppSuccess, setWhatsAppSuccess] = useState<string | null>(null);

  const [receiptUploading, setReceiptUploading] = useState(false);
  const [filePreview, setFilePreview] = useState<{
    url: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getExpeditedFilingStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        setDateOfFilingOfExpedited(
          parseDDMMYYYYToDate(data.dateOfFilingOfExpeditedApplication)
        );
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load expedited filing status");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    getLeadDetails(leadId)
      .then(({ lead }) => {
        if (lead?.phone) setPhoneNumber(lead.phone);
      })
      .catch(() => {});
  }, [leadId]);

  const handleSave = async () => {
    if (!leadId || saving) return;
    if (!dateOfFilingOfExpedited) {
      toast.error("Date of Filing of Expedited Application is required");
      return;
    }
    setSaving(true);
    try {
      const updated = await submitExpeditedFiling(leadId, {
        dateOfFilingOfExpeditedApplication: formatDateToISO(dateOfFilingOfExpedited),
      });
      setStatus(updated);
      setDateOfFilingOfExpedited(
        parseDDMMYYYYToDate(updated.dateOfFilingOfExpeditedApplication)
      );
      toast.success("Expedited filing saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadReceipt = async (file: File) => {
    if (!leadId) return;
    setReceiptUploading(true);
    try {
      const key = await uploadAcknowledgementPdf(
        file,
        `acknowledgement/${leadId}/expedited-receipt`
      );
      await saveAcknowledgementPdfKey(leadId, {
        type: "expedited_receipt",
        url: key,
      });
      const updated = await getExpeditedFilingStatus(leadId);
      setStatus(updated);
      toast.success("Trademarks Receipt for Expedited Application uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setReceiptUploading(false);
    }
  };

  const handleSendReceipt = async () => {
    const err = validateMobileNumber(phoneNumber);
    if (err) {
      toast.error(err);
      return;
    }
    setWhatsAppSuccess(null);
    setSendingReceipt(true);
    try {
      if (leadId) {
        const res = await sendExpeditedReceiptToClient(leadId);
        if (res?.waLink) window.open(res.waLink, "_blank", "noopener,noreferrer");
        setWhatsAppSuccess("Expedited receipt sent to client");
      } else {
        const waLink = buildWhatsAppLink(phoneNumber.trim(), "");
        if (waLink) window.open(waLink, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSendingReceipt(false);
    }
  };

  const handleSendAutomatedMessage = async () => {
    const err = validateMobileNumber(phoneNumber);
    if (err) {
      toast.error(err);
      return;
    }
    setWhatsAppSuccess(null);
    setSendingMessage(true);
    try {
      if (leadId) {
        const res = await sendExpeditedFilingAutomatedMessage(leadId);
        if (res?.waLink) window.open(res.waLink, "_blank", "noopener,noreferrer");
        setWhatsAppSuccess("Expedited filing message sent to client");
      } else {
        const waLink = buildWhatsAppLink(phoneNumber.trim(), "");
        if (waLink) window.open(waLink, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSendingMessage(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading expedited filing status...</p>
      </div>
    );
  }

  if (!status?.isActive) {
    return (
      <InactiveStepView
        title="Not Required"
        body='Expedited Application was marked as "NO" in confirmation stage. This step is inactive.'
        buttonText="Next Step"
        onButtonClick={() => onSuccess?.(leadId ?? "")}
      />
    );
  }

  return (
    <form
      id="filing-of-expedited-application-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
      className="flex flex-col w-full max-w-[1085px]"
      style={{
        fontFamily: "Inter, sans-serif",
        gap: 20,
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      <div
        className="flex items-center text-[#222222]"
        style={{ fontSize: 16, lineHeight: "20px" }}
      >
        Filing of Expedited Application
      </div>

      {/* Acknowledgement context (read-only) */}
      {status.acknowledgementNo && (
        <div className="flex flex-col flex-1 min-w-0 gap-1">
          <label className={LABEL}>Acknowledgement No.</label>
          <input
            type="text"
            value={status.acknowledgementNo}
            readOnly
            className={INPUT}
          />
        </div>
      )}

      {/* 3ca) Date of Filing of Expedited Application */}
      <div className="flex flex-col flex-1 min-w-0 gap-1 max-w-[300px]">
        <label className={LABEL}>Date of Filing of Expedited Application (3ca)</label>
        <DateTimePicker
          value={dateOfFilingOfExpedited}
          onChange={setDateOfFilingOfExpedited}
          placeholder="Select date"
          showTime={false}
        />
      </div>

      {/* Save & Continue is provided by the parent footer */}

      {/* Trademarks Receipt for Expedited Application (upload) */}
      <div className="flex min-w-0 flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-3 py-2.5 max-w-[520px]">
        <div className="flex flex-col gap-0.5">
          <p className="text-[12px] font-medium text-[#222222] md:text-xs">
            Trademarks Receipt for Expedited Application
          </p>
          <p className="text-[11px] text-[#6D6D6D]">
            Upload the expedited receipt PDF received from the registry to enable
            sending it to the client.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-[12px] md:text-xs ${
              status.expeditedReceiptPdfKey ? "text-[#16A34A]" : "text-[#6D6D6D]"
            }`}
          >
            {receiptUploading
              ? "Uploading…"
              : status.expeditedReceiptPdfKey
              ? "Receipt uploaded"
              : "No receipt uploaded yet"}
          </span>
          {status.expeditedReceiptPdfUrl && (
            <button
              type="button"
              onClick={() =>
                setFilePreview({
                  url: status.expeditedReceiptPdfUrl!,
                  name: "Expedited Trademarks Receipt.pdf",
                })
              }
              className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] md:text-xs`}
            >
              View
            </button>
          )}
          <button
            type="button"
            disabled={receiptUploading}
            onClick={() =>
              document.getElementById("expedited-receipt-input")?.click()
            }
            className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] disabled:opacity-50 md:text-xs`}
          >
            {receiptUploading
              ? "Uploading…"
              : status.expeditedReceiptPdfKey
              ? "Replace"
              : "Upload"}
          </button>
          <input
            id="expedited-receipt-input"
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUploadReceipt(f);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {/* WhatsApp section */}
      <div className="flex flex-col gap-3 mt-2">
        <div
          className="flex items-center text-[#222222]"
          style={{ fontSize: 16, lineHeight: "20px" }}
        >
          Send to Client
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <input
            type="tel"
            inputMode="numeric"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(formatPhoneInput(e.target.value))}
            placeholder="e.g. +919876543210 or 9876543210"
            className="w-full min-w-0 sm:w-[200px] h-9 px-2.5 py-2 text-xs text-[#000000] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
          />
          <button
            type="button"
            onClick={handleSendAutomatedMessage}
            disabled={sendingMessage}
            className={`${INPUT_GHOST} flex items-center justify-center cursor-pointer hover:bg-[#EEEEEE] transition-colors whitespace-nowrap disabled:opacity-50`}
            style={{ width: 240 }}
          >
            {sendingMessage ? "Sending…" : "Send Expedited Filing Message"}
          </button>
          <button
            type="button"
            onClick={handleSendReceipt}
            disabled={sendingReceipt || !status.expeditedReceiptPdfKey}
            className={`${INPUT_GHOST} flex items-center justify-center cursor-pointer hover:bg-[#EEEEEE] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
            style={{ width: 200 }}
            title={
              !status.expeditedReceiptPdfKey
                ? "Upload Trademarks Receipt for Expedited Application PDF first"
                : undefined
            }
          >
            {sendingReceipt ? "Sending…" : "Send Receipt to Client"}
          </button>
        </div>
        {whatsAppSuccess && (
          <p className="text-xs text-green-600">{whatsAppSuccess}</p>
        )}
      </div>

      <FilePreviewModal
        open={!!filePreview}
        onClose={() => setFilePreview(null)}
        name={filePreview?.name ?? ""}
        url={filePreview?.url}
        kind="pdf"
      />
    </form>
  );
}
